/**
 * Whale Positions
 * Table of Hyperliquid whale positions with net bias indicator
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassWhalePositions } from '@/hooks/use-coinglass';
import { formatCompact, formatPrice } from '@/lib/utils';
import type { Asset } from '@/stores/alert-store';

interface WhalePositionsProps {
  asset: Asset;
}

export function WhalePositionsTable({ asset }: WhalePositionsProps) {
  const { data, isLoading } = useCoinglassWhalePositions(asset);

  if (isLoading) return <LoadingSkeleton className="h-64" lines={6} />;

  const whales = data?.whalePositions;
  if (!whales?.positions?.length) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Whale Positions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No whale data</p>
        </CardContent>
      </Card>
    );
  }

  const biasDirection = whales.bias?.direction ?? 'neutral';
  const biasVariant = biasDirection === 'bullish' ? 'bullish' : biasDirection === 'bearish' ? 'bearish' : 'secondary';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Whale Positions</CardTitle>
          <Badge variant={biasVariant}>
            {biasDirection} ({(whales.bias?.netBias ?? 0) > 0 ? '+' : ''}{formatCompact(whales.bias?.netBias)})
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="max-h-[240px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="pb-1 text-left font-medium">Account</th>
                <th className="pb-1 text-right font-medium">Side</th>
                <th className="pb-1 text-right font-medium">Size</th>
                <th className="pb-1 text-right font-medium">Entry</th>
                <th className="pb-1 text-right font-medium">PnL</th>
              </tr>
            </thead>
            <tbody>
              {whales.positions.slice(0, 10).map((pos, i) => {
                const p = pos as unknown as Record<string, unknown>;
                const account = String(p.user ?? p.account ?? '');
                const size = Number(p.position_size ?? p.sizeDelta ?? 0);
                const side = size >= 0 ? 'long' : 'short';
                const entry = Number(p.entry_price ?? p.entryPrice ?? 0);
                const pnl = Number(p.unrealized_pnl ?? p.unrealizedPnl ?? 0);

                return (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-1 font-mono text-muted-foreground">
                      {account.slice(0, 6)}...{account.slice(-4)}
                    </td>
                    <td className={`py-1 text-right ${side === 'long' ? 'text-green-500' : 'text-red-500'}`}>
                      {side}
                    </td>
                    <td className="py-1 text-right">{formatCompact(Math.abs(size))}</td>
                    <td className="py-1 text-right">{formatPrice(entry)}</td>
                    <td className={`py-1 text-right ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      ${formatCompact(pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
